/**
 * EnvelopeReviewPage
 *
 * Pre-send confirmation step. Owner double-checks recipients, fields, and
 * expiration before flipping the envelope to `sent`. After this point the
 * envelope is read-only — switching back to a draft would invalidate every
 * outstanding signing token, which is a deliberate path we don't ship in v1.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, KeyRound, Send, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { HolderSwitcher, useHolderSwitcher } from '@/components/HolderSwitcher';
import { ArcheHeader } from '../../../components/ArcheHeader';
import {
  useBulkSend,
  useDocumentSignFeatures,
  useEnvelope,
  useSendEnvelope,
  useRecipients,
} from '../hooks';
import { formatCredits, formatDollars } from '@/lib/format-credits';
import { useSelectedHolder } from '../hooks/useSelectedHolder';
import { tierRequiresAccessCode } from '../assuranceTier';

/** People listed on review before "…and N more" — a CSV list can hold 500. */
const REVIEW_PEOPLE_SHOWN = 20;

export function EnvelopeReviewPage() {
  const { envelopeId } = useParams<{ envelopeId: string }>();
  const navigate = useNavigate();
  const { envelope, loading, fetching, error: loadError } = useEnvelope(envelopeId);
  const { recipients } = useRecipients(envelopeId);
  const send = useSendEnvelope(envelopeId ?? '');
  const bulkSend = useBulkSend(envelopeId ?? '');
  const { sendFeeCredits } = useDocumentSignFeatures();

  // The bulk send as the owner first saw it here. The envelope refetches (on
  // window focus, after saves), and an edit made elsewhere — another tab —
  // would otherwise refresh the fingerprint silently and be sent unseen. So
  // the first one is kept and sent; a different one blocks Send until the
  // owner confirms they have looked at the latest.
  //
  // Taken only once loading has SETTLED: arriving from the builder, the
  // envelope is often still refetching the owner's own last saves, and the
  // cached copy shown meanwhile is the one from before them. Kept per
  // envelope, so a reused page never compares two different envelopes.
  const currentFingerprint = envelope?.reviewFingerprint ?? null;
  const [reviewed, setReviewed] = useState<{ envelopeId: string; fingerprint: string } | null>(
    null,
  );
  const reviewedFingerprint =
    reviewed && reviewed.envelopeId === envelopeId ? reviewed.fingerprint : null;
  useEffect(() => {
    if (envelopeId && reviewedFingerprint === null && currentFingerprint !== null && !fetching) {
      setReviewed({ envelopeId, fingerprint: currentFingerprint });
    }
  }, [envelopeId, reviewedFingerprint, currentFingerprint, fetching]);
  const changedSinceOpened =
    reviewedFingerprint !== null &&
    currentFingerprint !== null &&
    currentFingerprint !== reviewedFingerprint;
  const acknowledgeLatest = () => {
    if (envelopeId && currentFingerprint) {
      setReviewed({ envelopeId, fingerprint: currentFingerprint });
    }
    // A refusal from the version before ("this send changed") no longer applies.
    bulkSend.reset();
  };

  // Active workspace chip in the header. Distinct from the envelope's own
  // holder (fixed at create-time) — the chip reflects the user's preference
  // for OTHER work and persists across arches.
  const { holderId: activeHolderId, setHolderId: setActiveHolderId } = useSelectedHolder();

  // Friendly display name for the envelope's holder — used in the
  // "Sending from" callout below. The envelope holder is immutable, so we
  // look it up via the same workspace list the chip uses; if the user can
  // no longer see this holder (e.g. revoked from the org) we fall back to
  // the raw holder id.
  const switcher = useHolderSwitcher();
  const envelopeWorkspaceName =
    switcher.options.find((o) => o.holderId === envelope?.holderId)?.displayName ?? null;

  if (!envelopeId) return null;

  const builderPath = `/a/arche_document_sign/envelope/${envelopeId}`;
  // An envelope that has already gone out (sent from another tab, say) has
  // nothing left to edit: Back goes to its detail page, not a read-only builder.
  const editable = envelope?.status === 'draft';
  const backToEdit = () => navigate(editable ? builderPath : `${builderPath}/detail`);

  // A retired bulk template (sent) or a deleted draft 404s. Say so rather
  // than keep showing a cached copy that would offer to send it again.
  if (loadError) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign">
          <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        </ArcheHeader>
        <div className="max-w-3xl mx-auto p-6 w-full space-y-3">
          <p className="text-sm text-destructive">{loadError}</p>
          <Button variant="outline" onClick={() => navigate('/a/arche_document_sign')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to envelopes
          </Button>
        </div>
      </div>
    );
  }

  if (loading || !envelope) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign">
          <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        </ArcheHeader>
        <div className="max-w-3xl mx-auto p-6 w-full space-y-3">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const sourceFiles = envelope.files.filter((f) => f.kind === 'source');
  const fieldsByRecipient: Record<string, number> = {};
  for (const f of envelope.fields) {
    fieldsByRecipient[f.recipientId] = (fieldsByRecipient[f.recipientId] ?? 0) + 1;
  }

  async function handleBulkSend() {
    // Send is disabled until the reviewed fingerprint is known; never send
    // without one — it is what makes the server refuse an unreviewed change.
    if (!reviewedFingerprint) return;
    try {
      // The envelope as shown here: the server refuses if it changed since.
      const result = await bulkSend.mutateAsync(reviewedFingerprint);
      // The send is recorded and this draft is retired. `replace`, so Back
      // from the overview cannot return to a review page for a draft that no
      // longer exists.
      navigate(`/a/arche_document_sign/bulk/${result.batchId}`, { replace: true });
    } catch {
      // Error surfaced via bulkSend.error below — nothing was recorded.
    }
  }

  async function handleSend() {
    try {
      const result = await send.mutateAsync();
      // The envelope IS sent at this point, whatever happened to the emails.
      // A failed dispatch has no retry — there is no signature-request resend
      // yet, and the raw signing link is never stored (only its hash), so
      // nothing on the envelope page can hand it over either. The only honest
      // recovery is the user knowing, by name, who may not have received a
      // link, and what they can actually do about it. Say that here, before
      // the confirmation screen reads as a clean success.
      const failed = result.deliveryFailures ?? [];
      if (failed.length > 0) {
        const names = failed.map(
          (f) => recipients.find((r) => r.recipientId === f.recipientId)?.name ?? 'a recipient',
        );
        toast({
          title: failed.length === 1 ? 'One signing email failed' : 'Some signing emails failed',
          description: `The envelope was sent, but we couldn’t email ${names.join(', ')}. A signing link can’t be resent yet — if it doesn’t arrive, void this envelope and send a new one.`,
          variant: 'destructive',
        });
      }
      // Confirmation screen — the builder URL is the wrong surface for a
      // non-draft envelope (it's the edit view) and a recipient who lands
      // on the detail view without seeing a confirmation can wonder
      // whether anything actually happened.
      navigate(`/a/arche_document_sign/envelope/${envelopeId}/sent`);
    } catch {
      // Error surfaced via send.error below
    }
  }

  /**
   * Signers still missing an access code on a Regulated envelope.
   *
   * The server enforces this inside `sendEnvelope`'s transaction and throws
   * "every signer needs an access code before it can be sent". Without a
   * check here that arrived as a red line under the Send button AFTER the
   * click, naming no one — leaving the sender to guess which of five people
   * it meant and navigate back to find out.
   *
   * `requiresAccessCode` is the server's own answer to "does this recipient
   * have a code", derived from whether a hash is stored. The code itself is
   * never returned and cannot be shown here; this list is a pointer back to
   * the builder, not a place to fix it.
   */
  const signersMissingCode = tierRequiresAccessCode(envelope.assuranceTier ?? 'standard')
    ? recipients.filter((r) => r.role === 'signer' && r.requiresAccessCode !== true)
    : [];

  // The page renders for any status — only the Send button is gated. Copy that
  // describes what sending WILL do has to know which of those it is.
  const isDraft = envelope.status === 'draft';
  // A team workspace's app grant reaches colleagues' envelopes too.
  const isSharedWorkspace = envelope.holderType !== 'personal';
  const isBulk = envelope.sendMode === 'bulk';
  const bulkPeople = envelope.bulkRecipients ?? [];
  // A bulk send's count and total come from this envelope, so never send
  // while a refresh that could change them is in flight.
  const canSend = isBulk
    ? isDraft &&
      bulkPeople.length > 0 &&
      !fetching &&
      reviewedFingerprint !== null &&
      !changedSinceOpened
    : isDraft && signersMissingCode.length === 0;
  const pending = send.isPending || bulkSend.isPending;
  const sendError = isBulk ? bulkSend.error : send.error;

  return (
    <div className="flex flex-col h-full">
      {/* No ReturnToOriginButton here, and that is deliberate. The builder and
          the sent page both offer the return; this page is the commit step,
          and its only exit is inward ("Back to edit") on purpose. An
          outward exit sitting beside Send — irreversible, billed, and mailing
          third parties — is a mis-click at the worst possible moment, and it
          buys little: review is a transient stop one click from the builder,
          which has the return. */}
      <ArcheHeader archeId="arche_document_sign" title="Document Sign">
        <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        <Button variant="outline" size="sm" onClick={backToEdit}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {editable ? 'Back to edit' : 'Back to envelope'}
        </Button>
      </ArcheHeader>
      <div className="max-w-3xl mx-auto p-6 w-full space-y-6">
        <div>
          <h1 className="text-xl font-semibold">Send "{envelope.title}"</h1>
          <p className="text-sm text-muted-foreground">
            Double-check the recipients and fields below. Once sent, you can void the envelope but
            cannot edit it.
          </p>
        </div>

        {/*
         * "Sending from" callout. The most important workspace check happens
         * here — Send is the irreversible action. If the user accidentally
         * built this envelope under the wrong workspace, this is their last
         * chance to notice before tokens are minted and emails go out.
         * Recovery from here is "Back to builder" → void the draft → start
         * over under the right workspace (the global chip).
         */}
        <section
          className="border-l-4 border-primary bg-primary/5 rounded-r-md p-4"
          aria-label="Workspace context"
        >
          <p className="text-sm">
            <span className="text-muted-foreground">Sending from: </span>
            <strong>{envelopeWorkspaceName ?? envelope.holderId}</strong>
            <span className="text-muted-foreground">
              {' '}
              ·{' '}
              {isBulk
                ? `${bulkPeople.length} separate ${bulkPeople.length === 1 ? 'copy' : 'copies'}`
                : `${recipients.length} ${recipients.length === 1 ? 'recipient' : 'recipients'}`}
            </span>
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Recipients will see this as coming from this workspace. The workspace can't be changed
            after sending.
          </p>
        </section>

        <section className="border rounded-md p-4 space-y-2">
          <h2 className="text-sm font-semibold">Documents</h2>
          {sourceFiles.length === 0 ? (
            <p className="text-sm text-destructive">No source documents attached.</p>
          ) : (
            <ul className="text-sm list-disc pl-4">
              {sourceFiles.map((f) => (
                <li key={f.fileId}>
                  {f.fileName}
                  {f.pageCount !== null && (
                    <span className="text-muted-foreground"> ({f.pageCount} pages)</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {isBulk ? (
          <section className="border rounded-md p-4 space-y-2" aria-label="Bulk send recipients">
            <h2 className="text-sm font-semibold">
              Bulk send to {bulkPeople.length} {bulkPeople.length === 1 ? 'person' : 'people'}
            </h2>
            <p className="text-xs text-muted-foreground">
              Each person gets their own envelope with {envelope.fields.length}{' '}
              {envelope.fields.length === 1 ? 'field' : 'fields'} to fill in, and never sees anyone
              else on this list. Each copy is charged as one envelope and gets its own copy of the
              documents. Copies are prepared and sent in the background — you can follow them on the
              next page.
            </p>
            {/* One click here is one fee per person — show the total before it. */}
            {sendFeeCredits !== null && sendFeeCredits > 0 && bulkPeople.length > 0 && (
              <p className="text-sm" data-testid="bulk-send-total">
                Total: {bulkPeople.length} × {formatCredits(sendFeeCredits)} credits ={' '}
                <strong>
                  {formatCredits(sendFeeCredits * bulkPeople.length)} credits (
                  {formatDollars(sendFeeCredits * bulkPeople.length)})
                </strong>
              </p>
            )}
            {bulkPeople.length === 0 ? (
              <p className="text-sm text-destructive">No one added yet.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {bulkPeople.slice(0, REVIEW_PEOPLE_SHOWN).map((p) => (
                  <li key={p.email}>
                    <strong>{p.name}</strong>
                    <span className="text-muted-foreground"> ({p.email})</span>
                  </li>
                ))}
                {bulkPeople.length > REVIEW_PEOPLE_SHOWN && (
                  <li className="text-muted-foreground">
                    …and {bulkPeople.length - REVIEW_PEOPLE_SHOWN} more
                  </li>
                )}
              </ul>
            )}
          </section>
        ) : (
          <section className="border rounded-md p-4 space-y-2">
            <h2 className="text-sm font-semibold">
              Recipients ({recipients.length}, {envelope.signingOrder} order)
            </h2>
            {recipients.length === 0 ? (
              <p className="text-sm text-destructive">No recipients added.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {recipients.map((r) => (
                  <li key={r.recipientId} className="flex justify-between gap-3">
                    <span>
                      {envelope.signingOrder === 'sequential' && (
                        <span className="text-xs text-muted-foreground mr-2">
                          #{r.routingOrder + 1}
                        </span>
                      )}
                      <strong>{r.name ?? r.email ?? 'Unnamed'}</strong>
                      {r.email && r.name && (
                        <span className="text-muted-foreground"> ({r.email})</span>
                      )}
                      <span className="text-xs text-muted-foreground ml-2">{r.role}</span>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {fieldsByRecipient[r.recipientId] ?? 0} fields
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {!isBulk && signersMissingCode.length > 0 && (
          <section
            className="rounded-md border border-amber-500/40 bg-amber-500/10 p-4 space-y-2"
            aria-label="Access codes required"
          >
            <h2 className="text-sm font-semibold flex items-center gap-2 text-amber-900 dark:text-amber-200">
              <KeyRound className="h-4 w-4" />
              Access codes needed before sending
            </h2>
            <p className="text-sm text-amber-900 dark:text-amber-200">
              This is a Consumer / regulated envelope, so every signer needs an access code. These
              signers do not have one yet:
            </p>
            <ul className="text-sm list-disc pl-5 text-amber-900 dark:text-amber-200">
              {signersMissingCode.map((r) => (
                <li key={r.recipientId}>{r.name ?? r.email ?? 'Unnamed signer'}</li>
              ))}
            </ul>
            <Button variant="outline" size="sm" onClick={backToEdit}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Set access codes
            </Button>
          </section>
        )}

        {/*
         * Cross-arche read disclosure (design §6.2).
         *
         * Sending is the moment the originating arche gains standing read
         * access to this envelope, so this is where the user is told — before
         * the irreversible click, not in a settings page afterwards. The grant
         * is implicit in pressing Send; what makes that legitimate is that the
         * wording describes exactly what is exposed, and that it is revocable.
         *
         * The name comes from the SERVER, resolved from the stored
         * `originArcheId`. That is the whole point: `originArcheId` is
         * host-derived, not cryptographically attested, so a forged origin has
         * to become visible at the moment it would matter. Naming a real arche
         * the user does not recognise is the signal.
         *
         * Says "status, recipients and signing activity" and stops there,
         * because that is exactly the projection the read verbs return — no
         * document bytes, and no recipient email addresses. If the DTO ever
         * grows a field, this sentence grows with it or it becomes a lie.
         *
         * TWO TENSES, because this page renders for a sent envelope too — only
         * the Send button is gated on `draft`, not the route. On a sent
         * envelope the grant already exists (written inside `sendEnvelope`'s
         * transaction), so the future-tense copy would tell the user the access
         * has not happened yet, on the one screen whose whole job is an
         * accurate disclosure.
         *
         * THE REACH IS THE WORKSPACE'S, NOT THIS ENVELOPE'S. The grant is one
         * row per (workspace, app), so it covers every sent envelope the app
         * started there — on a team workspace, colleagues' included, for any
         * member who uses the app — and a Send reactivates a grant someone
         * removed. "Envelopes you send from it" understated all three; the copy
         * says them. Mirrored on the Fias AI send card (`fias-ai-tools.ts`,
         * `appAccessDisclosure`) — change both together.
         */}
        {envelope.originArcheId && (
          <section
            className="rounded-md border border-blue-500/40 bg-blue-500/10 p-4 space-y-1"
            aria-label={isDraft ? 'App access after sending' : 'App access'}
          >
            <h2 className="text-sm font-semibold flex items-center gap-2 text-blue-900 dark:text-blue-200">
              <Share2 className="h-4 w-4" />
              {envelope.originArcheName ?? 'The app that started this request'}{' '}
              {isDraft ? 'will be able to follow this' : 'can follow this'}
            </h2>
            <p className="text-sm text-blue-900 dark:text-blue-200">
              {isDraft ? 'Once you send, ' : ''}
              <strong>
                {envelope.originArcheName ?? 'the app that started this request'}
              </strong>{' '}
              {isDraft ? 'will be able to see' : 'can see'} the status, recipients and signing
              activity of every signature request it started in{' '}
              {isSharedWorkspace
                ? 'this workspace — including ones your colleagues send — for anyone here who uses it,'
                : 'your personal workspace,'}{' '}
              until {isSharedWorkspace ? 'someone removes' : 'you remove'} its access in Vault →
              Arche Access. Sending another request it started turns that access back on. It never
              sees the documents themselves, and never a draft that hasn’t been sent.
            </p>
          </section>
        )}

        <section className="border rounded-md p-4 space-y-2">
          <h2 className="text-sm font-semibold">Expiration</h2>
          <p className="text-sm">
            Each signing link expires 30 days after sending. Recipients who haven't completed by
            then will need a new link.
          </p>
        </section>

        {/* Always mounted, so screen readers announce the text when it
            appears (a live region mounted together with its text often is
            not announced). */}
        <div id="bulk-send-changed" role="status">
          {isBulk && changedSinceOpened && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200 flex flex-wrap items-center justify-between gap-2">
              <span>
                This send changed since you opened this page — perhaps in another tab. The page now
                shows the latest version; check it before sending.
              </span>
              <Button variant="outline" size="sm" onClick={acknowledgeLatest}>
                I&apos;ve reviewed the changes
              </Button>
            </div>
          )}
        </div>

        {sendError && (
          <p className="text-sm text-destructive">
            {sendError instanceof Error ? sendError.message : 'Failed to send'}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {/* Back sits beside Send, where the eye already is. It was labelled
              "Cancel", which reads as "throw this envelope away" — so people
              used the browser's back button instead. It only returns to the
              builder; the draft is saved as it is edited. */}
          <Button variant="outline" onClick={backToEdit} disabled={pending}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {editable ? 'Back to edit' : 'Back to envelope'}
          </Button>
          <Button
            onClick={isBulk ? handleBulkSend : handleSend}
            disabled={!canSend || pending}
            aria-describedby={isBulk && changedSinceOpened ? 'bulk-send-changed' : undefined}
          >
            <Send className="h-4 w-4 mr-2" />
            {pending
              ? 'Sending…'
              : isBulk
                ? `Send ${bulkPeople.length} ${bulkPeople.length === 1 ? 'copy' : 'copies'}`
                : 'Send for signature'}
          </Button>
        </div>
      </div>
    </div>
  );
}
